import * as React from "react";
import { useAtom } from "jotai";
import {
  expandedStopRootsAtom,
  stopLocationsByRootAtom,
  stopLocationsInflightAtom,
} from "../atoms";
import { fetchLocationsForRoot } from "../api/stopLocations";
import { getActiveTpid } from "../api/timetablePeriods";
import type { Stop } from "../types";
const keyOf = (tpid: string, rootId: string) => `${tpid}|${rootId}`;

export function useStopLocations() {
  const [expanded, setExpanded] = useAtom(expandedStopRootsAtom);
  const [cache, setCache] = useAtom(stopLocationsByRootAtom);
  const [inflight, setInflight] = useAtom(stopLocationsInflightAtom);

  const ensureLoaded = React.useCallback(
    async (rootId: string) => {
      const tpidVal = await getActiveTpid();
      const tpid = String(tpidVal ?? "auto");
      const k = keyOf(tpid, rootId);

      const cached = cache.get(k);
      if (cached) return cached.stops;

      const running = inflight.get(k);
      if (running) return await running;

      const p = fetchLocationsForRoot(rootId)
        .then((stops) => {
          setCache((prev) => {
            const next = new Map(prev);
            next.set(k, { tpid, stops });
            return next;
          });
          setInflight((prev) => {
            const next = new Map(prev);
            next.delete(k);
            return next;
          });
          return stops;
        })
        .catch((e) => {
          setInflight((prev) => {
            const next = new Map(prev);
            next.delete(k);
            return next;
          });
          throw e;
        });

      setInflight((prev) => {
        const next = new Map(prev);
        next.set(k, p);
        return next;
      });

      return await p;
    },
    [cache, inflight, setCache, setInflight]
  );

  const toggleRoot = React.useCallback(
    async (rootId: string, checked: boolean) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (checked) next.add(rootId);
        else next.delete(rootId);
        return next;
      });
      if (checked) await ensureLoaded(rootId);
    },
    [ensureLoaded, setExpanded]
  );

  const getLocationsFor = React.useCallback(
    (rootId: string, preferTpid?: string) => {
      let fallback: Stop[] | null = null;
      for (const [k, v] of cache) {
        const [tpid, rid] = k.split("|");
        if (rid !== rootId) continue;
        if (preferTpid && tpid === preferTpid) return v.stops;
        if (!fallback) fallback = v.stops;
      }
      return fallback ?? [];
    },
    [cache]
  );

  return { expanded, toggleRoot, ensureLoaded, getLocationsFor };
}
